<app-split-pane
  storageKey="split.whatsapp"
  [initialLeftRatio]="0.7"
  [collapseBreakpointPx]="1024"
  [collapsedOnSmallScreens]="true"
>
  <ng-container left>
    <app-split-pane-vertical storageKey="split.whatsapp.left" [initialTopRatio]="0.7">
      <ng-container top>
        <app-chat
          [suggestions]="whatsappSuggestions"
          [context]="whatsappContext"
          placeholder="Ask about WhatsApp integration and project updates..."
        ></app-chat>
      </ng-container>
      <ng-container bottom>
        <app-history scopeKey="whatsapp.chat"></app-history>
      </ng-container>
    </app-split-pane-vertical>
  </ng-container>

  <ng-container right>
    <app-split-pane-vertical storageKey="split.whatsapp.right" [initialTopRatio]="0.7">
      <ng-container top>
        <div class="container py-3 d-flex flex-column gap-3 whatsapp-page">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h2 class="mb-0">WhatsApp</h2>
              <div class="text-muted small">Project-scoped messaging workspace</div>
            </div>
            <div class="btn-group" role="group" aria-label="Communication view">
              <button class="btn btn-outline-secondary btn-sm" type="button" [class.active]="view() === 'all'" (click)="view.set('all')">All</button>
              <button class="btn btn-outline-secondary btn-sm" type="button" [class.active]="view() === 'messages'" (click)="view.set('messages')">Messages</button>
              <button class="btn btn-outline-secondary btn-sm" type="button" [class.active]="view() === 'calls'" (click)="view.set('calls')">Calls</button>
            </div>
          </div>

          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <div class="fw-semibold mb-1">Phone linking</div>
                  @if (loadingLinkStatus()) {
                    <div class="text-muted small">Checking WhatsApp status...</div>
                  } @else if (!hasProjectPhoneMapping()) {
                    <div class="text-warning small">No Meta phone number is mapped for this project yet.</div>
                  } @else if (linkedPhone()) {
                    <div class="text-success small">Linked number: {{ linkedPhone() }}</div>
                  } @else {
                    <div class="text-muted small">No linked cell number for this project.</div>
                  }
                </div>
                <div class="d-flex gap-2">
                  <button class="btn btn-outline-secondary btn-sm" type="button" (click)="refreshIntegrationStatus()" [disabled]="loadingLinkStatus()">Refresh status</button>
                  <button class="btn btn-outline-danger btn-sm" type="button" (click)="unlinkPhone()" [disabled]="unlinkingPhone() || !linkedPhone()">Unlink</button>
                </div>
              </div>

              <div class="row g-2 mt-2">
                <div class="col-md-5">
                  <label class="form-label">Cell number</label>
                  <input class="form-control" [value]="phoneInput()" (input)="phoneInput.set($any($event.target).value)" placeholder="+15551234567" />
                </div>
                <div class="col-md-3">
                  <label class="form-label">OTP code</label>
                  <input class="form-control" [value]="otpCodeInput()" (input)="otpCodeInput.set($any($event.target).value)" placeholder="123456" />
                </div>
                <div class="col-md-4 d-flex align-items-end gap-2">
                  <button class="btn btn-outline-secondary" type="button" (click)="requestOtp()" [disabled]="sendingOtp() || !hasProjectPhoneMapping()">Request OTP</button>
                  <button class="btn btn-primary" type="button" (click)="verifyOtp()" [disabled]="verifyingOtp() || !hasProjectPhoneMapping()">Verify</button>
                </div>
              </div>
              @if (otpExpiresAt()) {
                <div class="text-muted small mt-2">Latest OTP expires at {{ otpExpiresAt() | date:'shortTime' }}</div>
              }
            </div>
          </div>

          @if (view() !== 'calls') {
            <div class="card">
              <div class="card-body">
                <div class="fw-semibold mb-1">Messages</div>
                <div class="text-muted small">
                  WhatsApp message history for the currently selected project.
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Direction</th>
                        <th>Status</th>
                        <th>Phone</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (loadingMessages()) {
                        <tr>
                          <td colspan="5" class="text-muted small">Loading project messages...</td>
                        </tr>
                      } @else {
                        @for (m of filteredMessages(); track m.id) {
                          <tr>
                            <td class="text-nowrap">{{ m.timestamp | date:'short' }}</td>
                            <td>{{ m.direction }}</td>
                            <td>{{ m.status }}</td>
                            <td class="text-muted small">{{ m.phoneNumber || '—' }}</td>
                            <td>{{ m.text || '—' }}</td>
                          </tr>
                        }
                        @if (filteredMessages().length === 0) {
                          <tr>
                            <td colspan="5" class="text-center text-muted">No WhatsApp messages found</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
                <div class="p-2 border-top text-end">
                  <button class="btn btn-outline-secondary btn-sm" type="button" (click)="refreshMessages()" [disabled]="loadingMessages()">Refresh messages</button>
                </div>
              </div>
            </div>
          }

          @if (view() !== 'messages') {
            <div class="card">
              <div class="card-body">
                <div class="row g-2 align-items-end">
                  <div class="col-md-3">
                    <label class="form-label">Call scope</label>
                    <select class="form-select" [value]="scope()" (change)="scope.set($any($event.target).value); refreshCalls()">
                      <option value="org">Active organization</option>
                      <option value="project">Selected project</option>
                      <option value="me">My calls</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Search calls</label>
                    <input class="form-control" [value]="search()" (input)="search.set($any($event.target).value)" placeholder="Search summary, status, from/to, ids..." />
                  </div>
                  <div class="col-md-3 text-end">
                    <button class="btn btn-outline-secondary" type="button" (click)="refreshCalls()" [disabled]="loadingCalls()">Refresh calls</button>
                  </div>
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Started</th>
                        <th>Channel</th>
                        <th>Status</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Org</th>
                        <th>Project</th>
                        <th>Summary</th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (loadingCalls()) {
                        <tr>
                          <td colspan="8" class="text-muted small">Loading call records…</td>
                        </tr>
                      } @else {
                        @for (r of filteredCalls(); track r.id) {
                          <tr>
                            <td class="text-nowrap">{{ r.startedAt | date:'short' }}</td>
                            <td>{{ r.channel }}</td>
                            <td>{{ r.status }}</td>
                            <td class="text-muted small">{{ r.from || '—' }}</td>
                            <td class="text-muted small">{{ r.to || '—' }}</td>
                            <td class="text-muted small">{{ r.organizationId || '—' }}</td>
                            <td class="text-muted small">{{ r.projectId || '—' }}</td>
                            <td>{{ r.summary || '—' }}</td>
                          </tr>
                        }
                        @if (filteredCalls().length === 0) {
                          <tr>
                            <td colspan="8" class="text-center text-muted">No call records found</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }
        </div>
      </ng-container>
      <ng-container bottom>
        <app-history scopeKey="whatsapp.main"></app-history>
      </ng-container>
    </app-split-pane-vertical>
  </ng-container>
</app-split-pane>
